import { type BasePageParams } from '@/app/urls/url-mapping';
import { type UrlEntry, type UrlRegion } from '@/types';

export type RichTextParams = { urlSegments: string[] } & BasePageParams;

// In next iteration should use similar approach as author page for i18n slugs
const PREFIXES = [
  'aviso-legal',
  'conditions-d-utilisation',
  'datenschutzerklaerung',
  'impressum',
  'imprint',
  'nutzungsbedingungen',
  'politica-de-privacidade',
  'politique-de-confidentialite',
  'privacy-policy',
  'terminos-condiciones',
  'termos-de-uso',
  'terms-of-use',
] as const;

type Prefix = (typeof PREFIXES)[number];

export type RichTextPages = `richText/${Prefix}`;

export const urls: Record<
  RichTextPages,
  Partial<Record<UrlRegion, UrlEntry<Prefix>>>
> = {
  'richText/aviso-legal': {
    es: 'aviso-legal',
    'es-en': 'privacy-policy',
  },
  'richText/conditions-d-utilisation': {
    fr: 'conditions-d-utilisation',
    'fr-en': 'terms-of-use',
  },
  'richText/datenschutzerklaerung': {
    de: 'datenschutzerklaerung',
    'de-en': 'privacy-policy',
  },
  'richText/impressum': {
    de: 'impressum',
    'de-en': 'imprint',
  },
  'richText/imprint': {
    de: 'impressum',
    'de-en': 'imprint',
  },
  'richText/nutzungsbedingungen': {
    de: 'nutzungsbedingungen',
    'de-en': 'terms-of-use',
  },
  'richText/politica-de-privacidade': {
    pt: 'politica-de-privacidade',
    'pt-en': 'privacy-policy',
  },
  'richText/politique-de-confidentialite': {
    fr: 'politique-de-confidentialite',
    'fr-en': 'privacy-policy',
  },
  'richText/privacy-policy': {
    de: 'datenschutzerklaerung',
    'de-en': 'privacy-policy',
    es: 'aviso-legal',
    'es-en': 'privacy-policy',
    fr: 'politique-de-confidentialite',
    'fr-en': 'privacy-policy',
    gb: 'privacy-policy',
    nl: 'privacy-policy',
    'nl-en': 'privacy-policy',
    pt: 'politica-de-privacidade',
    'pt-en': 'privacy-policy',
    us: 'privacy-policy',
    ww: 'privacy-policy',
  },
  'richText/terminos-condiciones': {
    es: 'terminos-condiciones',
    'es-en': 'terms-of-use',
  },
  'richText/termos-de-uso': {
    pt: 'termos-de-uso',
    'pt-en': 'terms-of-use',
  },
  'richText/terms-of-use': {
    de: 'nutzungsbedingungen',
    'de-en': 'terms-of-use',
    es: 'terminos-condiciones',
    'es-en': 'terms-of-use',
    fr: 'conditions-d-utilisation',
    'fr-en': 'terms-of-use',
    gb: 'terms-of-use',
    nl: 'terms-of-use',
    'nl-en': 'terms-of-use',
    pt: 'termos-de-uso',
    'pt-en': 'terms-of-use',
    us: 'terms-of-use',
    ww: 'terms-of-use',
  },
};

export const getRichTextPageType = (params: RichTextParams): RichTextPages => {
  const {
    urlSegments: [prefix],
  } = params;
  return `richText/${prefix}` as RichTextPages;
};

export const isRichTextPrefix = (segment: string): segment is Prefix =>
  PREFIXES.includes(segment as Prefix);
